import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { NewsItem, fetchPublicNewsItem } from '@/services/news';

function shareUrl(platform: 'facebook' | 'twitter' | 'line', pageUrl: string, title: string) {
  const encodedUrl = encodeURIComponent(pageUrl);
  const encodedTitle = encodeURIComponent(title);
  if (platform === 'facebook') return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  if (platform === 'twitter') return `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
  return `https://social-plugins.line.me/lineit/share?url=${encodedUrl}&text=${encodedTitle}`;
}

export default function NewsDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [item, setItem] = useState<NewsItem | null>(null);
  const [relatedItems, setRelatedItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!id) {
      navigate('/news', { replace: true });
      return;
    }
    setLoading(true);
    fetchPublicNewsItem(id)
      .then((data) => {
        if (!alive) return;
        if (!data.item) {
          navigate('/news', { replace: true });
          return;
        }
        setItem(data.item);
        setRelatedItems(data.relatedItems);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id, navigate]);

  useEffect(() => {
    if (!item) return;
    const previousTitle = document.title;
    document.title = `${item.title}｜富聆聽健康`;
    return () => {
      document.title = previousTitle;
    };
  }, [item]);

  async function handleCopyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="mx-auto max-w-4xl px-6 py-10 md:px-10 md:py-16 lg:px-16">
            <div className="h-4 w-40 animate-pulse rounded bg-[#f1ece4]" />
            <div className="mt-5 h-8 w-3/4 animate-pulse rounded bg-[#f1ece4]" />
            <div className="mt-4 h-4 w-32 animate-pulse rounded bg-[#f1ece4]" />
            <div className="mt-8 space-y-4">
              <div className="h-5 animate-pulse rounded bg-[#f1ece4]" />
              <div className="h-5 w-5/6 animate-pulse rounded bg-[#f1ece4]" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!item) return null;

  const pageUrl = window.location.href;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="mx-auto max-w-4xl px-6 py-8 md:px-10 md:py-12 lg:px-16">
          {/* 麵包屑 */}
          <div className="flex flex-wrap items-center gap-1.5 text-sm text-[#9A8A7A]">
            <button type="button" onClick={() => navigate('/')} className="hover:text-[#1B3D2A] transition-colors">首頁</button>
            <i className="ri-arrow-right-s-line" />
            <button type="button" onClick={() => navigate('/news')} className="hover:text-[#1B3D2A] transition-colors">最新消息</button>
            {item.category && (
              <>
                <i className="ri-arrow-right-s-line" />
                <button type="button" onClick={() => navigate(`/news?category=${item.newsCategoryId ?? ''}`)} className="hover:text-[#1B3D2A] transition-colors">
                  {item.category}
                </button>
              </>
            )}
          </div>

          {/* 標頭 */}
          <div className="mt-4 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <h1 className="text-xl md:text-3xl font-bold leading-snug text-[#1B3D2A]">{item.title}</h1>
            <span className="shrink-0 text-sm text-[#9A8A7A] md:pt-1">{item.date}</span>
          </div>

          {/* 分享 */}
          <div className="mt-4 flex items-center gap-2">
            <span className="text-xs font-semibold tracking-wider text-[#9A8A7A] uppercase mr-1">分享</span>
            <a
              href={shareUrl('facebook', pageUrl, item.title)}
              target="_blank"
              rel="noreferrer"
              className="w-8 h-8 rounded-full bg-[#FAF8F5] border border-[#E8E0D6] flex items-center justify-center text-[#1B3D2A] hover:bg-[#1B3D2A] hover:text-white transition-colors"
              aria-label="分享到 Facebook"
            >
              <i className="ri-facebook-fill text-sm" />
            </a>
            <a
              href={shareUrl('twitter', pageUrl, item.title)}
              target="_blank"
              rel="noreferrer"
              className="w-8 h-8 rounded-full bg-[#FAF8F5] border border-[#E8E0D6] flex items-center justify-center text-[#1B3D2A] hover:bg-[#1B3D2A] hover:text-white transition-colors"
              aria-label="分享到 Twitter"
            >
              <i className="ri-twitter-x-fill text-sm" />
            </a>
            <a
              href={shareUrl('line', pageUrl, item.title)}
              target="_blank"
              rel="noreferrer"
              className="w-8 h-8 rounded-full bg-[#FAF8F5] border border-[#E8E0D6] flex items-center justify-center text-[#1B3D2A] hover:bg-[#1B3D2A] hover:text-white transition-colors"
              aria-label="分享到 Line"
            >
              <i className="ri-line-fill text-sm" />
            </a>
            <button
              type="button"
              onClick={handleCopyLink}
              className="w-8 h-8 rounded-full bg-[#FAF8F5] border border-[#E8E0D6] flex items-center justify-center text-[#1B3D2A] hover:bg-[#1B3D2A] hover:text-white transition-colors"
              aria-label="複製連結"
            >
              <i className={`text-sm ${copied ? 'ri-check-line' : 'ri-link'}`} />
            </button>
            {copied && <span className="text-xs text-[#5A6B5A]">已複製連結</span>}
          </div>

          <div className="my-6 h-px bg-[#E8E0D6]" />

          {/* 內容 */}
          <div
            className="prose prose-lg max-w-none text-gray-700 prose-headings:text-[#1B3D2A] prose-a:text-[#1B3D2A] prose-img:rounded-xl prose-iframe:aspect-video prose-iframe:w-full prose-iframe:rounded-xl"
            dangerouslySetInnerHTML={{ __html: item.content || '' }}
          />

          {/* 底部導航 */}
          <div className="mt-10 pt-8 border-t border-[#E8E0D6] flex flex-col sm:flex-row items-center justify-between gap-4">
            <button
              onClick={() => navigate('/news')}
              className="text-[#1B3D2A] text-sm font-medium flex items-center gap-2 hover:underline py-2"
            >
              <i className="ri-arrow-left-line" /> 返回最新消息列表
            </button>
            <button
              onClick={() => navigate('/booking')}
              className="bg-[#1B3D2A] text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-[#143026] transition-colors whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-calendar-check-line" />
              預約免費聽力檢測
            </button>
          </div>
        </div>

        {/* 相關消息 */}
        {relatedItems.length > 0 && (
          <div className="bg-[#FAF8F5] py-10 md:py-14">
            <div className="mx-auto max-w-4xl px-6 md:px-10 lg:px-16">
              <h3 className="text-lg font-bold text-[#1B3D2A] mb-4">同分類其他消息</h3>
              <ul className="bg-white rounded-2xl border border-[#E8E0D6] divide-y divide-[#E8E0D6]">
                {relatedItems.map((related) => (
                  <li key={related.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/news/${related.slug || related.id}`)}
                      className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:text-[#D4A574] transition-colors"
                    >
                      <span className="truncate text-sm font-medium text-[#1B3D2A]">{related.title}</span>
                      <span className="shrink-0 text-xs text-[#9A8A7A]">{related.date}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
